import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ChevronRight, Flame, Target, ShoppingBag } from "lucide-react";
import AppShell from "../components/AppShell";
import MascotCoach from "../components/MascotCoach";
import { useProfile } from "../context/ProfileContext";
import { GAMES, MASCOTS, pick } from "../data/gameData";
import { api } from "../lib/api";

export default function Hub() {
  const navigate = useNavigate();
  const { profile } = useProfile();
  const [quests, setQuests] = useState([]);
  const [greeting] = useState(() => (profile ? pick(MASCOTS[profile.mascot]?.welcome || ["Ready to play?"]) : ""));

  useEffect(() => {
    if (profile) api.getQuests(profile.id).then((q) => setQuests(q.items)).catch(() => {});
  }, [profile]);

  if (!profile) return null;
  const featured = GAMES.slice(0, 3);
  const questDone = quests.filter((q) => q.progress >= q.goal).length;

  return (
    <AppShell>
      <div className="mb-5 flex items-start justify-between gap-3">
        <div>
          <div className="text-[#A8B2D1] text-sm">Welcome back,</div>
          <h1 className="text-4xl font-black leading-none">{profile.name}!</h1>
        </div>
        <MascotCoach mascotId={profile.mascot} message={greeting} />
      </div>

      {/* Daily streak / event tile */}
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="glass rounded-3xl p-5 mb-5 relative overflow-hidden"
        style={{ background: "linear-gradient(120deg, rgba(255,42,122,0.28), rgba(0,229,255,0.15))" }}>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-[#FFB27A] font-bold">
              <Flame size={20} className="text-[#FF7A00]" /> {profile.streak}-day streak
            </div>
            <div className="text-sm text-white/85 mt-1">
              {questDone}/{quests.length} daily quests done
            </div>
          </div>
          <button
            data-testid="hub-rewards-btn"
            onClick={() => navigate("/rewards")}
            className="btn-3d px-4 py-2.5 bg-white/15 text-white font-bold flex items-center gap-1">
            Rewards <ChevronRight size={16} />
          </button>
        </div>
      </motion.div>

      {/* Big play CTA */}
      <button
        data-testid="hub-play-btn"
        onClick={() => navigate("/map")}
        className="w-full btn-3d py-5 mb-3 flex items-center justify-center gap-3 text-white text-xl font-black"
        style={{ background: "linear-gradient(90deg,#FF2A7A,#A855F7)", boxShadow: "0 8px 0 rgba(0,0,0,.35)" }}>
        <Target size={26} /> Open World Map
      </button>

      <button
        data-testid="hub-shop-btn"
        onClick={() => navigate("/shop")}
        className="w-full btn-3d py-3.5 mb-6 flex items-center justify-center gap-2 text-white font-bold glass">
        <ShoppingBag size={20} className="text-[#FFD600]" /> Cosmetic Shop
      </button>

      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold">Quick Play</h2>
        <button data-testid="hub-allgames-btn" onClick={() => navigate("/map")}
                className="text-sm text-[#00E5FF] font-semibold">All games →</button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {featured.map((g, i) => {
          const Icon = Icons[g.icon] || Target;
          return (
            <motion.button
              key={g.id}
              data-testid={`quickplay-${g.id}`}
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 * i }}
              onClick={() => navigate(`/play/${g.id}`)}
              className="glass rounded-3xl p-4 text-left press relative overflow-hidden">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3"
                   style={{ background: `${g.color}22` }}>
                <Icon size={26} style={{ color: g.color }} />
              </div>
              <div className="font-bold">{g.name}</div>
              <div className="text-xs text-[#A8B2D1]">{g.tagline}</div>
            </motion.button>
          );
        })}
      </div>
    </AppShell>
  );
}
